/**
 * Single shared resolver for post-auth landing routes.
 * Prefer canonical portal / selectedAccount fields over bare user.role.
 */

export function resolveAuthenticatedHome(auth = {}) {
  const user = auth.user || auth;
  const requiresSelection = Boolean(
    auth.requiresAccountSelection
    || auth.requires_account_selection
    || user?.requires_account_selection
  );

  if (requiresSelection) {
    return '/choose-account';
  }

  const role = String(
    auth.effectiveRole
    || user?.effective_role
    || user?.active_account?.role
    || user?.role
    || ''
  ).toLowerCase();

  // Self Repair roles always land on /dashboard — never Agency — even when
  // legacy membership type/portal still say "agency" and account_id equals an agency id.
  if (['self_repair', 'self_repair_customer', 'self_repair_user', 'premium_user'].includes(role)) {
    return '/dashboard';
  }

  const portal =
    auth.portal
    || auth.selectedAccount?.portal
    || user?.portal
    || user?.active_account?.portal
    || user?.selected_account?.portal
    || null;

  const accountType =
    auth.accountType
    || auth.selectedAccount?.account_type
    || user?.account_type
    || user?.active_account?.type
    || null;

  // agency_id is metadata only — never take self_repair active_account.account_id as agency.
  const agencyId = (() => {
    if (portal === 'self_repair' || accountType === 'self_repair') return null;
    return (
      auth.agencyId
      || auth.selectedAccount?.agency_id
      || user?.agency_id
      || (
        ['agency', 'agency_staff', 'funding_hq'].includes(String(portal || ''))
        || ['agency', 'subaccount', 'funding_hq'].includes(String(accountType || ''))
          ? (user?.active_account?.account_id || null)
          : null
      )
      || null
    );
  })();

  if (portal === 'platform' || accountType === 'platform') {
    return '/platform/dashboard';
  }

  if (portal === 'agency' || portal === 'funding_hq' || accountType === 'agency' || accountType === 'funding_hq') {
    if (agencyId && agencyId !== 'default_agency') {
      if (accountType === 'funding_hq') {
        return `/agency/${agencyId}/funding`;
      }
      return `/agency/${agencyId}/dashboard`;
    }
    return '/agency/dashboard';
  }

  if (portal === 'agency_staff') {
    if (agencyId && agencyId !== 'default_agency') {
      return `/agency/${agencyId}/dashboard`;
    }
    return '/staff/dashboard';
  }

  if (portal === 'client' || portal === 'managed_client') {
    return '/client/dashboard';
  }

  if (portal === 'self_repair' || accountType === 'self_repair') {
    return '/dashboard';
  }

  // Fallbacks when portal fields are missing but role/agency_id are set.
  if (['platform_owner', 'platform_admin', 'super_admin'].includes(role)) {
    return '/platform/dashboard';
  }

  if (
    ['agency_owner', 'agency_admin', 'agency_staff', 'agency_user', 'owner', 'admin', 'staff'].includes(role)
    && agencyId
    && agencyId !== 'default_agency'
  ) {
    return `/agency/${agencyId}/dashboard`;
  }

  if (['managed_client'].includes(role)) {
    return '/client/dashboard';
  }

  if (['client'].includes(role)) {
    return '/dashboard';
  }

  return '/unauthorized';
}

export default resolveAuthenticatedHome;
